import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { formatDateTime } from '../utils/stringUtils';

const IdentitiesPage: React.FC = () => {
  const { state, getCurrentStats } = useApp();
  const navigate = useNavigate();
  const { did: didParam } = useParams<{ did?: string }>();
  const [expandedIdentityDids, setExpandedIdentityDids] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Record<string, Set<string>>>({});
  const [didDocumentContent, setDidDocumentContent] = useState<any>(null);
  const [loadingDidDocument, setLoadingDidDocument] = useState(false);
  const identityRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
  const [searchTerm, setSearchTerm] = useState('');

  const stats = getCurrentStats();
  const identities = stats?.identities || [];

  // Filter identities based on search term
  const filteredIdentities = React.useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return identities;
    
    const searchLower = trimmedSearch.toLowerCase();
    return identities.filter(identity => 
      identity.name?.toLowerCase().includes(searchLower) ||
      identity.did?.toLowerCase().includes(searchLower) ||
      identity.channel_name?.toLowerCase().includes(searchLower) ||
      identity.identity_hash?.toLowerCase().includes(searchLower)
    );
  }, [identities, searchTerm]);

  // Handle deep linking to a specific identity
  useEffect(() => {
    if (didParam) {
      const decodedDid = decodeURIComponent(didParam);
      // Expand the identity
      setExpandedIdentityDids(new Set([decodedDid]));
      setExpandedSections({
        [decodedDid]: new Set(['summary'])
      });
      
      // Scroll to the identity after a short delay to ensure rendering
      setTimeout(() => {
        const element = identityRefs.current[decodedDid];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [didParam]);

  // Toggle identity details expansion
  const toggleIdentityDetails = (did: string) => {
    setExpandedIdentityDids(prev => {
      const newSet = new Set(prev);
      if (newSet.has(did)) {
        newSet.delete(did);
        // Clean up expanded sections for this identity
        setExpandedSections(prevSections => {
          const newSections = { ...prevSections };
          delete newSections[did];
          return newSections;
        });
        // Navigate back to identities list when closing
        navigate('/identities');
      } else {
        newSet.add(did);
        // Initialize with summary section open
        setExpandedSections(prevSections => ({
          ...prevSections,
          [did]: new Set(['summary'])
        }));
        // Navigate to the deep link URL when expanding
        navigate(`/identities/${encodeURIComponent(did)}`);
      }
      return newSet;
    });
  };

  // Toggle a specific section within an identity
  const toggleSection = (did: string, section: string) => {
    setExpandedSections(prev => {
      const sections = prev[did] || new Set();
      const newSections = new Set(sections);
      if (newSections.has(section)) {
        newSections.delete(section);
      } else {
        newSections.add(section);
      }
      return {
        ...prev,
        [did]: newSections
      };
    });
  };

  const isSectionExpanded = (did: string, section: string) => {
    return expandedSections[did]?.has(section) || false;
  };

  // Copy deep link to clipboard
  const copyDeepLink = (did: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const encodedDid = encodeURIComponent(did);
    const url = `${window.location.origin}${window.location.pathname}#/identities/${encodedDid}`;
    navigator.clipboard.writeText(url);
  };

  // Load DID document for identity
  const loadDidDocumentForIdentity = async (did: string) => {
    setLoadingDidDocument(true);
    try {
      const response = await fetch(`/api/v1/identity/resolve-did?did=${encodeURIComponent(did)}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch DID document: ${response.status}`);
      }
      const didDoc = await response.json();
      setDidDocumentContent(didDoc);
    } catch (error) {
      console.error('Error loading DID document:', error);
      setDidDocumentContent({ error: 'Failed to load DID document' });
    } finally {
      setLoadingDidDocument(false);
    }
  };

  const getBadgeClass = (badge?: string) => {
    switch (badge) {
      case 'NEW':
        return 'badge-success';
      case 'ACTIVE':
        return 'badge-primary';
      default:
        return '';
    }
  };



  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Identities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
        <div>
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => navigate('/identities/integrations')}
          >
            <i className="fas fa-plug"></i> Integrations
          </button>
        </div>
      </div>

      {identities.length === 0 ? (
        <div className="card shadow mb-4">
          <div className="card-body text-center">
            <div className="text-muted">
              <i className="fas fa-users fa-3x mb-3"></i>
              <p>No agent identities found.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-users"></i> All Agent Identities ({filteredIdentities.length}{searchTerm && ` of ${identities.length}`})
            </h6>
          </div>
          <div className="card-body">
            {filteredIdentities.length === 0 ? (
              <div className="text-center">
                <div className="text-muted">
                  <i className="fas fa-search fa-3x mb-3"></i>
                  <p>No identities match your search.</p>
                </div>
              </div>
            ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>DID</th>
                    <th>Channel</th>
                    <th>Created</th>
                    <th>Usage Count</th>
                    <th>Last Used</th>
                    <th>Status</th>
                    <th style={{width: '50px'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIdentities.map((identity) => {
                    const isExpanded = expandedIdentityDids.has(identity.did);
                    return (
                    <React.Fragment key={identity.did}>
                      <tr 
                        ref={(el) => { identityRefs.current[identity.did] = el; }}
                        style={{cursor: 'pointer'}} 
                        onClick={() => toggleIdentityDetails(identity.did)}
                      >
                        <td>
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} mr-2`}></i>
                          <code style={{fontSize: '0.75rem'}}>{identity.did}</code>
                        </td>
                        <td>
                          {identity.channel_name && identity.channel_config_id ? (
                            <small>
                              <a 
                                href={`#/channels/${identity.channel_config_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/channels/${identity.channel_config_id}`);
                                }}
                                className="text-primary"
                              >
                                {identity.channel_name}
                              </a>
                            </small>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <small>{identity.created_at ? formatDateTime(identity.created_at, true) : 'Never'}</small>
                        </td>
                        <td>
                          <small>{identity.usage_count || identity.use_count || 0}</small>
                        </td>
                        <td>
                          <small>{(identity.last_used_at || identity.last_used) ? formatDateTime((identity.last_used_at || identity.last_used)!, true) : 'Never'}</small>
                        </td>
                        <td>
                          {identity.is_local !== undefined ? (
                            <>
                              <span className={`badge ${identity.is_local ? 'badge-info' : 'badge-warning'}`}>
                                {identity.is_local ? 'LOCAL' : 'REMOTE'}
                              </span>
                              {!identity.is_local && identity.verified && (
                                <span className="badge badge-success ms-1" style={{marginLeft: '0.75rem'}}>
                                  VERIFIED
                                </span>
                              )}
                            </>
                          ) : identity.badge ? (
                            <span className={`badge ${getBadgeClass(identity.badge)}`}>
                              {identity.badge}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-link p-0"
                            onClick={(e) => copyDeepLink(identity.did, e)}
                            title="Copy link to this identity"
                          >
                            <i className="fas fa-link text-muted"></i>
                          </button>
                        </td>
                      </tr>
                      {expandedIdentityDids.has(identity.did) && (
                        <tr onClick={(e) => e.stopPropagation()}>
                          <td colSpan={7} style={{backgroundColor: '#f8f9fc', padding: 0}}>
                            <div className="p-3">
                              <div className="accordion" id={`identityAccordion-${identity.did}`}>
                                {/* Summary */}
                                <div className="card mb-2">
                                  <div 
                                    className="card-header py-1 bg-light" 
                                    style={{cursor: 'pointer'}}
                                    onClick={() => toggleSection(identity.did, 'summary')}
                                  >
                                    <small className="mb-0 text-muted">
                                      <i className={`fas fa-chevron-${isSectionExpanded(identity.did, 'summary') ? 'down' : 'right'} mr-2`}></i>
                                      <i className="fas fa-info-circle"></i> Summary
                                    </small>
                                  </div>
                                  {isSectionExpanded(identity.did, 'summary') && (
                                    <div className="card-body py-2 px-3">
                                      <div className="row">
                                        <div className="col-md-6">
                                          <div className="mb-2">
                                            <small className="font-weight-bold text-muted">DID:</small>
                                            <div><small><code style={{fontSize: '0.75rem'}}>{identity.did}</code></small></div>
                                          </div>
                                          <div className="mb-2">
                                            <small className="font-weight-bold text-muted">Identity Hash:</small>
                                            <div><small>{identity.identity_hash}</small></div>
                                          </div>
                                          {identity.channel_name && identity.channel_config_id && (
                                            <div className="mb-2">
                                              <small className="font-weight-bold text-muted">Channel:</small>
                                              <div><small>
                                                <a 
                                                  href={`#/channels/${identity.channel_config_id}`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/channels/${identity.channel_config_id}`);
                                                  }}
                                                  className="text-primary"
                                                >
                                                  {identity.channel_name}
                                                </a>
                                              </small></div>
                                            </div>
                                          )}
                                        </div>
                                        <div className="col-md-6">
                                          <div className="mb-2">
                                            <small className="font-weight-bold text-muted">Created:</small>
                                            <div><small>{identity.created_at ? formatDateTime(identity.created_at, true) : 'Never'}</small></div>
                                          </div>
                                          <div className="mb-2">
                                            <small className="font-weight-bold text-muted">Usage Count:</small>
                                            <div><small><strong>{identity.usage_count || identity.use_count || 0}</strong> {((identity.usage_count || identity.use_count || 0) === 1) ? 'time' : 'times'}</small></div>
                                          </div>
                                          <div className="mb-2">
                                            <small className="font-weight-bold text-muted">Last Used:</small>
                                            <div><small>{(identity.last_used_at || identity.last_used) ? formatDateTime((identity.last_used_at || identity.last_used)!, true) : 'Never'}</small></div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Identity Field Values */}
                                {identity.agent_identity && (
                                  <div className="card mb-2">
                                    <div 
                                      className="card-header py-1 bg-light" 
                                      style={{cursor: 'pointer'}}
                                      onClick={() => toggleSection(identity.did, 'fields')}
                                    >
                                      <small className="mb-0 text-muted">
                                        <i className={`fas fa-chevron-${isSectionExpanded(identity.did, 'fields') ? 'down' : 'right'} mr-2`}></i>
                                        <i className="fas fa-id-badge"></i> Identity Field Values
                                      </small>
                                    </div>
                                    {isSectionExpanded(identity.did, 'fields') && (
                                      <div className="card-body py-2 px-3">
                                        <p className="text-muted small mb-2">
                                          <i className="fas fa-info-circle"></i> These fields marked with x-identity were used to create this identity mapping
                                        </p>
                                        {(() => {
                                          const identityFields = identity.agent_identity;
                                          
                                          if (!identityFields || typeof identityFields !== 'object' || Object.keys(identityFields).length === 0) {
                                            return (
                                              <p className="text-muted">
                                                <i className="fas fa-exclamation-circle"></i> No identity fields stored for this identity
                                              </p>
                                            );
                                          }
                                          
                                          const fieldEntries = Object.entries(identityFields).map(([key, value]) => ({ key, value }));
                                          
                                          return (
                                            <table className="table table-sm table-borderless mb-0">
                                              <tbody>
                                                {fieldEntries.map(({ key, value }) => (
                                                  <tr key={key}>
                                                    <td style={{ width: '40%' }}>
                                                      <strong className="text-primary">{key}</strong>
                                                    </td>
                                                    <td style={{ width: '60%' }}>
                                                      <code className="text-dark">
                                                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                      </code>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Identity Field Values (Full JSON) */}
                                {identity.agent_identity && (
                                  <div className="card mb-2">
                                    <div 
                                      className="card-header py-1 bg-light" 
                                      style={{cursor: 'pointer'}}
                                      onClick={() => toggleSection(identity.did, 'json')}
                                    >
                                      <small className="mb-0 text-muted">
                                        <i className={`fas fa-chevron-${isSectionExpanded(identity.did, 'json') ? 'down' : 'right'} mr-2`}></i>
                                        <i className="fas fa-robot"></i> Identity Field Values (Full JSON)
                                      </small>
                                    </div>
                                    {isSectionExpanded(identity.did, 'json') && (
                                      <div className="card-body py-2 px-3">
                                        <pre className="mb-0"><code>{JSON.stringify(identity.agent_identity, null, 2)}</code></pre>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* DID Document */}
                                <div className="card mb-2">
                                  <div 
                                    className="card-header py-1 bg-light" 
                                    style={{cursor: 'pointer'}}
                                    onClick={() => {
                                      toggleSection(identity.did, 'did');
                                      if (!isSectionExpanded(identity.did, 'did') && !didDocumentContent && !loadingDidDocument) {
                                        loadDidDocumentForIdentity(identity.did);
                                      }
                                    }}
                                  >
                                    <small className="mb-0 text-muted">
                                        <i className={`fas fa-chevron-${isSectionExpanded(identity.did, 'did') ? 'down' : 'right'} mr-2`}></i>
                                      <i className="fas fa-file-alt"></i> DID Document
                                    </small>
                                  </div>
                                  {isSectionExpanded(identity.did, 'did') && (
                                    <div className="card-body py-2 px-3">
                                      {loadingDidDocument ? (
                                        <div className="text-center text-muted">
                                          <i className="fas fa-spinner fa-spin"></i> Loading DID document...
                                        </div>
                                      ) : didDocumentContent?.error ? (
                                        <div className="text-center text-danger">
                                          <i className="fas fa-exclamation-triangle"></i> {didDocumentContent.error}
                                        </div>
                                      ) : didDocumentContent ? (
                                        <pre className="mb-0"><code>{JSON.stringify(didDocumentContent, null, 2)}</code></pre>
                                      ) : (
                                        <div className="text-center text-muted">
                                          Click to load DID document
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );})}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentitiesPage;